"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';

interface CookiePreferences {
	necessary: boolean;
	analytics: boolean;
}

export function CookieConsent() {
	const [showBanner, setShowBanner] = useState(false);
	const [showSettings, setShowSettings] = useState(false);
	const [preferences, setPreferences] = useState<CookiePreferences>({
		necessary: true, // Always true, can't be disabled
		analytics: false,
	});

	useEffect(() => {
		// Check if user has already made a choice
		const consent = localStorage.getItem('cookie-consent');
		if (!consent) {
			// Show banner after a short delay for better UX
			setTimeout(() => setShowBanner(true), 1000);
		} else {
			// Load saved preferences
			try {
				const savedPrefs = JSON.parse(consent);
				setPreferences(savedPrefs);
			} catch (e) {
				// If parsing fails, show banner again
				setShowBanner(true);
			}
		}
	}, []);

	const handleAcceptAll = () => {
		const newPreferences: CookiePreferences = {
			necessary: true,
			analytics: true,
		};
		setPreferences(newPreferences);
		localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
		localStorage.setItem('cookie-consent-date', new Date().toISOString());
		setShowBanner(false);
		setShowSettings(false);
		
		// Initialize analytics if accepted (placeholder for future analytics)
		if (newPreferences.analytics) {
			// Add analytics initialization here when ready
		}
	};

	const handleRejectAll = () => {
		const newPreferences: CookiePreferences = {
			necessary: true,
			analytics: false,
		};
		setPreferences(newPreferences);
		localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
		localStorage.setItem('cookie-consent-date', new Date().toISOString());
		setShowBanner(false);
		setShowSettings(false);
	};

	const handleSavePreferences = () => {
		localStorage.setItem('cookie-consent', JSON.stringify(preferences));
		localStorage.setItem('cookie-consent-date', new Date().toISOString());
		setShowBanner(false);
		setShowSettings(false);
		
		// Initialize analytics if accepted
		if (preferences.analytics) {
			// Add analytics initialization here when ready
		}
	};

	const handleToggleAnalytics = () => {
		setPreferences((prev) => ({
			...prev,
			analytics: !prev.analytics,
		}));
	};

	if (!showBanner) return null;

	return (
		<div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-slide-up">
			<div className="mx-auto max-w-4xl bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-4 md:p-6">
				{!showSettings ? (
					// Simple banner view
					<div className="flex flex-col md:flex-row items-start md:items-center gap-4">
						<div className="flex-1">
							<h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
								🍪 Vi använder cookies
							</h3>
							<p className="text-sm text-slate-600 dark:text-slate-400">
								Vi använder cookies för att förbättra din upplevelse, analysera trafik och personalisera innehåll. 
								Nödvändiga cookies krävs för att webbplatsen ska fungera korrekt. 
								<a href="/privacy" className="text-primary-600 dark:text-primary-400 hover:underline ml-1">
									Läs mer i vår integritetspolicy
								</a>
							</p>
						</div>
						<div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
							<button
								onClick={() => setShowSettings(true)}
								className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors whitespace-nowrap"
							>
								Anpassa
							</button>
							<button
								onClick={handleRejectAll}
								className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors whitespace-nowrap"
							>
								Avvisa alla
							</button>
							<button
								onClick={handleAcceptAll}
								className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-500 hover:bg-primary-700 dark:hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap"
							>
								Godkänn alla
							</button>
						</div>
					</div>
				) : (
					// Settings view
					<div className="space-y-4">
						<h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
							Cookie-inställningar
						</h3>
						<p className="text-sm text-slate-600 dark:text-slate-400">
							Välj vilka typer av cookies du vill tillåta. Du kan ändra dessa inställningar när som helst.
						</p>
						
						<div className="space-y-3">
							{/* Necessary Cookies */}
							<div className="flex items-start justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
								<div className="flex-1">
									<div className="flex items-center gap-2 mb-1">
										<h4 className="font-medium text-slate-900 dark:text-slate-100">
											Nödvändiga cookies
										</h4>
										<span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded">
											Alltid aktiv
										</span>
									</div>
									<p className="text-xs text-slate-600 dark:text-slate-400">
										Dessa cookies är nödvändiga för att webbplatsen ska fungera. De används för autentisering och säkerhet.
									</p>
								</div>
								<input
									type="checkbox"
									checked={preferences.necessary}
									disabled
									className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 ml-4 cursor-not-allowed opacity-50"
								/>
							</div>

							{/* Analytics Cookies */}
							<div className="flex items-start justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
								<div className="flex-1">
									<div className="flex items-center gap-2 mb-1">
										<h4 className="font-medium text-slate-900 dark:text-slate-100">
											Analytiska cookies
										</h4>
									</div>
									<p className="text-xs text-slate-600 dark:text-slate-400">
										Dessa cookies hjälper oss att förstå hur besökare använder webbplatsen genom att samla in anonymiserad information.
									</p>
								</div>
								<input
									type="checkbox"
									checked={preferences.analytics}
									onChange={handleToggleAnalytics}
									className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 ml-4 cursor-pointer"
								/>
							</div>
						</div>

						<div className="flex flex-col sm:flex-row gap-2 pt-2">
							<button
								onClick={() => setShowSettings(false)}
								className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
							>
								Avbryt
							</button>
							<button
								onClick={handleSavePreferences}
								className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-500 hover:bg-primary-700 dark:hover:bg-primary-600 rounded-lg transition-colors"
							>
								Spara inställningar
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

